/**
 * @description
 * @author librajt
 * @example
 *
 *  controller:
 *     this.slider = new RangeSlider({
 *         parentEl: this.parentRef,
 *         buttonEl: '.j_button',
 *         onRangeChange: (percent) => {
 *             this.props.onChange(percent);
 *         },
 *         onSizeChange: (maxWidth) => {
 *             this.setState({
 *                 maxWidth,
 *             });
 *         },
 *     });
 *     this.slider.run();
 *
 *  view:
 *     const valuePercent = (value || 0) / (max - min);
 *     return (
 *         <div className="slider-bar-wrap j_parent">
 *             <div className="slider-btn j_button" style={{
 *                 left: `${valuePercent * 100}%`,
 *             }}></div>
 *             <div className="slider-value" style={{
 *                 width: `${valuePercent * 100}%`,
 *             }}>
 *                 <div className="fill-liquid" style={{width: `${this.state.maxWidth}px`}}></div>
 *             </div>
 *         </div>
 *     );
 */

import { $document, $window } from '@umu/umu-dom';
import { debounce } from '@umu/umu-util';
import * as UA from '@umu/umu-ua';

export interface ISliderOptions {
    parentEl: React.RefObject<HTMLDivElement>;
    buttonEl: string;
    onRangeChange(percent: number): void;
    onSizeChange?(maxWidth: number): void;
}

const EVENT_DOWN = 'ontouchstart' in $window ? 'touchstart' : 'mousedown';
const EVENT_MOVE = 'ontouchmove' in $window ? 'touchmove' : 'mousemove';
const EVENT_UP = 'ontouchend' in $window ? 'touchend' : 'mouseup';

function getTouches(e: any, parentEl: HTMLElement) {
    const rect = parentEl.getBoundingClientRect();
    const ret = { x: 0, y: 0 };
    if (UA.isMobile && e.touches && e.touches[0]) {
        e = e as TouchEvent;
        ret.x = e.touches[0].clientX - rect.left;
        ret.y = e.touches[0].clientY - rect.top;
    } else {
        e = e as MouseEvent;
        ret.x = e.clientX - rect.left;
        ret.y = e.clientX - rect.top;
    }
    return ret;
}

export class RangeSlider {
    private options: ISliderOptions;
    private buttonEl: HTMLElement = document.createElement('div');
    private parentEl: HTMLElement = document.createElement('div');
    private maxX: number = 0;
    private startX: number = 0;
    private stopX: number = 0;

    constructor(options: ISliderOptions) {
        this.options = Object.assign({}, options);

        this.updateValue = this.updateValue.bind(this);
        this.updateBoundary = debounce(this.updateBoundary.bind(this), 30);

        this.resizeEvent = this.resizeEvent.bind(this);

        this.touchEventDown = this.touchEventDown.bind(this);
        this.touchEventMove = this.touchEventMove.bind(this);
        this.touchEventUp = this.touchEventUp.bind(this);

        this.touchEventLaunch = this.touchEventLaunch.bind(this);
        this.touchEventUnload = this.touchEventUnload.bind(this);
    }

    public run() {
        this.init();
    }

    public destroy() {
        this.parentEl.removeEventListener(EVENT_DOWN, this.touchEventDown, false);
        this.buttonEl.removeEventListener(EVENT_DOWN, this.touchEventDown, false);
        this.parentEl.removeEventListener(EVENT_UP, this.touchEventUp, false);
        $window.removeEventListener('resize', this.resizeEvent, false);
    }

    private getParentEl() {
        return this.options.parentEl.current;
    }

    private getButtonEl() {
        const parentEl = this.getParentEl();
        if (!parentEl) {
            return null;
        }
        return parentEl.querySelector(this.options.buttonEl) as HTMLElement;
    }

    private init() {
        const buttonEl = this.getButtonEl();
        if (!buttonEl) {
            console.error(`找不到 ${this.options.buttonEl} 元素`);
            return;
        }
        this.buttonEl = buttonEl;

        const parentEl = this.getParentEl();
        if (!parentEl) {
            console.error(`找不到 ${this.options.parentEl} 元素`);
            return;
        }
        this.parentEl = parentEl;

        // start
        setTimeout(this.updateBoundary, 0);
        this.bindEvent();
    }

    private updateValue() {
        let delta = this.stopX - this.startX;

        // 防止超出边界
        if (delta > this.maxX) {
            delta = this.maxX;
        } else if (delta < 0) {
            delta = 0;
        }

        const percent = delta / this.maxX;
        this.options.onRangeChange(percent);
    }

    private bindEvent() {
        this.buttonEl.addEventListener(EVENT_DOWN, this.touchEventDown, false);
        this.parentEl.addEventListener(EVENT_DOWN, this.touchEventDown, false);
        this.parentEl.addEventListener(EVENT_UP, this.touchEventUp, false);
        $window.addEventListener('resize', this.resizeEvent, false);
    }

    private resizeEvent() {
        this.updateBoundary();
    }

    private updateBoundary() {
        const rect = this.parentEl!.getBoundingClientRect();
        this.startX = 0;
        this.maxX = rect.width;
        const onSizeChange = this.options.onSizeChange;
        if (onSizeChange) {
            onSizeChange(this.maxX);
        }
    }

    // touch events
    private touchEventDown(e: Event) {
        this.preventDefault(e);
        this.touchEventUnload();
        this.touchEventLaunch();
        const { x } = getTouches(e, this.parentEl);
        this.stopX = x;
    }

    private touchEventMove(e: Event | MouseEvent | TouchEvent) {
        this.preventDefault(e);
        const { x } = getTouches(e, this.parentEl);
        this.stopX = x;
        this.updateValue();
    }

    private touchEventUp(e: Event | MouseEvent | TouchEvent) {
        this.preventDefault(e);
        this.updateValue();
        this.touchEventUnload();
    }

    private preventDefault(e: Event | MouseEvent | TouchEvent) {
        // [Intervention] Unable to preventDefault inside passive event listener due to target being treated as passive.
        // See https://www.chromestatus.com/features/5093566007214080
        // e.preventDefault();
        e.stopPropagation();
    }

    private touchEventLaunch() {
        $document.addEventListener(EVENT_MOVE, this.touchEventMove, false);
        $document.addEventListener(EVENT_UP, this.touchEventUp, false);
    }

    private touchEventUnload() {
        $document.removeEventListener(EVENT_MOVE, this.touchEventMove, false);
        $document.removeEventListener(EVENT_UP, this.touchEventUp, false);
    }
}

export default RangeSlider;
